import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreateTariffInput, TariffDto } from '@parking/shared';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { enumLabel } from '../shared/i18n/enumLabel';
import { PageHeader } from '../shared/ui/PageHeader';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { AppModal } from '../shared/ui/AppModal';
import { StatusBadge } from '../shared/ui/StatusBadge';

type TariffForm = {
  code: string;
  name: string;
  kind: NonNullable<CreateTariffInput['kind']>;
  parkingId: string;
  firstHourCents: string;
  extraHourCents: string;
  maxCents: string;
};

const emptyForm: TariffForm = {
  code: '',
  name: '',
  kind: 'HOUR',
  parkingId: '',
  firstHourCents: '250',
  extraHourCents: '200',
  maxCents: '2500',
};

function formFromTariff(tariff: TariffDto): TariffForm {
  const sorted = [...tariff.rules].sort((a, b) => a.sequence - b.sequence);
  const first = sorted[0];
  const extra = sorted[1] ?? sorted[0];
  return {
    code: tariff.code,
    name: tariff.name,
    kind: tariff.kind as NonNullable<CreateTariffInput['kind']>,
    parkingId: tariff.parkingId ?? '',
    firstHourCents: String(first?.priceCents ?? 0),
    extraHourCents: String(extra?.priceCents ?? 0),
    maxCents: String(extra?.maxChargeCents ?? first?.maxChargeCents ?? 0),
  };
}

function rulesFromForm(form: TariffForm) {
  return [
    {
      sequence: 1,
      fromMinute: 0,
      toMinute: 60,
      priceCents: Number(form.firstHourCents) || 0,
    },
    {
      sequence: 2,
      fromMinute: 60,
      toMinute: null,
      priceCents: Number(form.extraHourCents) || 0,
      maxChargeCents: Number(form.maxCents) || null,
    },
  ];
}

export function TariffsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canRead = useAuthStore((s) => s.hasPermission('tariffs:read'));
  const canWrite = useAuthStore((s) => s.hasPermission('tariffs:write'));

  const [dialog, setDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TariffForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  const tariffs = useQuery({
    queryKey: ['tariffs'],
    queryFn: () => api.listTariffs(),
    enabled: canRead,
  });

  const parkings = useQuery({
    queryKey: ['parkings'],
    queryFn: () => api.listParkings(),
    enabled: dialog,
  });

  const closeDialog = () => {
    setDialog(false);
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
  };

  const openCreate = () => {
    setFormError(null);
    setEditingId(null);
    setForm(emptyForm);
    setDialog(true);
  };

  const openEdit = (tariff: TariffDto) => {
    setFormError(null);
    setEditingId(tariff.id);
    setForm(formFromTariff(tariff));
    setDialog(true);
  };

  const saveTariff = useMutation({
    mutationFn: async () => {
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        kind: form.kind,
        parkingId: form.parkingId || null,
        rules: rulesFromForm(form),
      };
      if (editingId) {
        return api.updateTariff(editingId, payload);
      }
      return api.createTariff(payload);
    },
    onSuccess: async () => {
      closeDialog();
      await qc.invalidateQueries({ queryKey: ['tariffs'] });
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const deleteTariff = useMutation({
    mutationFn: (id: string) => api.deleteTariff(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tariffs'] });
    },
  });

  if (!canRead) {
    return <div className="alert alert-warning">{t('tariffs.forbidden')}</div>;
  }

  const rows = tariffs.data?.data ?? [];
  const columns = [
    t('tariffs.code'),
    t('tariffs.name'),
    t('tariffs.kind'),
    t('tariffs.priority'),
    t('tariffs.active'),
    t('tariffs.rules'),
    ...(canWrite ? [t('common.actions')] : []),
  ];

  return (
    <>
      <PageHeader
        actions={
          canWrite ? (
            <button type="button" className="btn btn-primary" onClick={openCreate}>
              {t('tariffs.create')}
            </button>
          ) : undefined
        }
      />

      {tariffs.isError && (
        <div className="alert alert-danger">{(tariffs.error as Error).message}</div>
      )}

      <Card>
        <ParkingDataTable
          storageKey="tariffs"
          columns={columns}
          refreshKey={rows.map((r) => r.id).join('|')}
          actionsColumn={canWrite}
          galleryItems={rows.map((tariff) => ({
            id: tariff.id,
            icon: 'bi-tag',
            iconTone: tariff.isActive ? ('success' as const) : ('secondary' as const),
            title: tariff.code,
            subtitle: tariff.name,
            meta: `${enumLabel(t, 'tariffKind', tariff.kind)} · prio ${tariff.priority} · ${tariff.rules
              .map(
                (r) =>
                  `${r.fromMinute}-${r.toMinute ?? '∞'}: ${(r.priceCents / 100).toFixed(2)}€`,
              )
              .join(' · ')}`,
            badge: (
              <StatusBadge tone={tariff.isActive ? 'success' : 'secondary'}>
                {tariff.isActive ? t('common.activeOn') : t('common.activeOff')}
              </StatusBadge>
            ),
            actions: canWrite ? (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-primary"
                  aria-label="edit"
                  onClick={() => openEdit(tariff)}
                >
                  <i className="bi bi-pencil" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-danger"
                  aria-label="delete"
                  onClick={() => {
                    if (window.confirm(t('tariffs.confirmDelete'))) {
                      deleteTariff.mutate(tariff.id);
                    }
                  }}
                >
                  <i className="bi bi-trash" />
                </button>
              </>
            ) : undefined,
          }))}
        >
          {rows.map((tariff) => (
            <tr key={tariff.id}>
              <td className="fw-semibold">{tariff.code}</td>
              <td>{tariff.name}</td>
              <td>{enumLabel(t, 'tariffKind', tariff.kind)}</td>
              <td>{tariff.priority}</td>
              <td>
                <StatusBadge tone={tariff.isActive ? 'success' : 'secondary'}>
                  {tariff.isActive ? t('common.activeOn') : t('common.activeOff')}
                </StatusBadge>
              </td>
              <td>
                {tariff.rules
                  .map(
                    (r) =>
                      `${r.fromMinute}-${r.toMinute ?? '∞'}: ${(r.priceCents / 100).toFixed(2)}€`,
                  )
                  .join(' · ')}
              </td>
              {canWrite && (
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-primary"
                    aria-label="edit"
                    onClick={() => openEdit(tariff)}
                  >
                    <i className="bi bi-pencil" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-danger"
                    aria-label="delete"
                    onClick={() => {
                      if (window.confirm(t('tariffs.confirmDelete'))) {
                        deleteTariff.mutate(tariff.id);
                      }
                    }}
                  >
                    <i className="bi bi-trash" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <AppModal
        open={dialog}
        onClose={closeDialog}
        title={editingId ? t('tariffs.edit') : t('tariffs.create')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={closeDialog}>
              {t('tariffs.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saveTariff.isPending}
              onClick={() => saveTariff.mutate()}
            >
              {t('tariffs.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {formError && <div className="alert alert-danger mb-0">{formError}</div>}
          <div>
            <label className="form-label">{t('tariffs.code')}</label>
            <input
              className="form-control"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="form-label">{t('tariffs.name')}</label>
            <input
              className="form-control"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="form-label">{t('tariffs.kind')}</label>
            <select
              className="form-select"
              value={form.kind}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  kind: e.target.value as NonNullable<CreateTariffInput['kind']>,
                }))
              }
            >
              {['HOUR', 'MINUTE', 'DAILY'].map((k) => (
                <option key={k} value={k}>
                  {enumLabel(t, 'tariffKind', k)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">{t('tariffs.parking')}</label>
            <select
              className="form-select"
              value={form.parkingId}
              onChange={(e) => setForm((f) => ({ ...f, parkingId: e.target.value }))}
            >
              <option value="">{t('tariffs.global')}</option>
              {(parkings.data?.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">{t('tariffs.firstHour')}</label>
            <input
              className="form-control"
              type="number"
              value={form.firstHourCents}
              onChange={(e) => setForm((f) => ({ ...f, firstHourCents: e.target.value }))}
            />
            <div className="form-text">{t('tariffs.centsHint')}</div>
          </div>
          <div>
            <label className="form-label">{t('tariffs.extraHour')}</label>
            <input
              className="form-control"
              type="number"
              value={form.extraHourCents}
              onChange={(e) => setForm((f) => ({ ...f, extraHourCents: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('tariffs.maxCharge')}</label>
            <input
              className="form-control"
              type="number"
              value={form.maxCents}
              onChange={(e) => setForm((f) => ({ ...f, maxCents: e.target.value }))}
            />
          </div>
        </div>
      </AppModal>
    </>
  );
}
